import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import * as apiService from "../../api";

const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);
  const infoAfiliado = useSelector((state) => state.afiliado.infoAfiliado);

  // Función para verificar si el usuario necesita completar el onboarding
  const checkOnboardingStatus = async (codigoAfiliado, forceCheck = false) => {
    if (!codigoAfiliado) {
      console.warn("🚫 No hay código de afiliado para verificar onboarding");
      return false;
    }

    try {
      // Verificar si ya se mostró el onboarding en esta sesión
      if (!forceCheck) {
        const onboardingShown = sessionStorage.getItem(
          `onboarding_shown_${codigoAfiliado}`
        );

        if (onboardingShown === "true") {
          return false;
        }
      }

      // Obtener datos de contacto del afiliado
      let datosContacto;

      try {
        const response = await apiService.afiliado.getDatosContactos(
          codigoAfiliado
        );
        datosContacto = response.data;
      } catch (apiError) {
        console.warn(
          "⚠️ Error al obtener datos del backend:",
          apiError.message
        );
        return false;
      }

      // Verificar el nuevo campo debeActualizarDatosContacto
      if (datosContacto.hasOwnProperty("debeActualizarDatosContacto")) {
        const debeActualizar = datosContacto.debeActualizarDatosContacto;

        if (debeActualizar === true) {
          return true;
        } else {
          return false;
        }
      } else {
        // Si no existe el campo, usar la lógica original como fallback

        // Criterios originales para mostrar el onboarding:
        // 1. No tiene teléfono celular
        // 2. No tiene correo electrónico
        // 3. No tiene dirección completa (región, provincia, municipio)
        const criterios = {
          telefonoCelular: !datosContacto.telefonoCelular,
          correoElectronico: !datosContacto.correoElectronico,
          idRegion: !datosContacto.idRegion,
          idProvincia: !datosContacto.idProvincia,
          idMunicipio: !datosContacto.idMunicipio,
        };

        const needsOnboarding =
          criterios.telefonoCelular ||
          criterios.correoElectronico ||
          criterios.idRegion ||
          criterios.idProvincia ||
          criterios.idMunicipio;

        return needsOnboarding;
      }
    } catch (error) {
      console.error("❌ Error verificando estado del onboarding:", error);
      return false;
    }
  };

  // Función para marcar el onboarding como completado
  const completeOnboarding = () => {
    if (infoAfiliado.codigoAfiliado) {
      sessionStorage.setItem(
        `onboarding_shown_${infoAfiliado.codigoAfiliado}`,
        "true"
      );
    }
    setShowOnboarding(false);
  };

  // Función para activar la verificación de onboarding
  const triggerOnboardingCheck = async (infoAfiliado) => {
    if (infoAfiliado.codigoAfiliado) {
      setIsCheckingOnboarding(true);

      try {
        // Delay para asegurar que el componente esté completamente montado
        setTimeout(async () => {
          const needsOnboarding = await checkOnboardingStatus(
            infoAfiliado.codigoAfiliado,
            false
          );
          setShowOnboarding(needsOnboarding);
          setIsCheckingOnboarding(false);
        }, 1500); // Aumentando el delay a 1.5 segundos
      } catch (error) {
        console.error("❌ Error en triggerOnboardingCheck:", error);
        setIsCheckingOnboarding(false);
      }
    } else {
      setIsCheckingOnboarding(false);
    }
  };

  // Función para resetear el estado del onboarding (útil para testing o admin)
  const resetOnboarding = () => {
    if (infoAfiliado.codigoAfiliado) {
      sessionStorage.removeItem(
        `onboarding_shown_${infoAfiliado.codigoAfiliado}`
      );
      setShowOnboarding(false);
    }
  };

  // Función nueva para limpiar TODOS los onboardings de sessionStorage (para testing)
  const clearAllOnboardingFlags = () => {
    // Obtener todas las claves del sessionStorage
    const keys = Object.keys(sessionStorage);

    // Filtrar las que empiecen con 'onboarding_shown_'
    const onboardingKeys = keys.filter((key) =>
      key.startsWith("onboarding_shown_")
    );

    // Eliminar cada una
    onboardingKeys.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    setShowOnboarding(false);
  };

  // Función para mostrar el onboarding manualmente
  const showOnboardingManually = () => {
    setShowOnboarding(true);
  };

  // Verificar automáticamente cuando se carga el componente o cambia el afiliado
  // DESHABILITADO: Ya no verificamos automáticamente porque el nuevo flujo
  // es Login -> verifica -> redirecciona a página de onboarding
  /*
  useEffect(() => {
    if (infoAfiliado.codigoAfiliado) {
      // Delay para asegurar que el componente esté completamente montado
      const timer = setTimeout(() => {
        checkOnboardingStatus();
      }, 2000); // Aumentando el delay a 2 segundos

      return () => clearTimeout(timer);
    }
  }, [infoAfiliado.codigoAfiliado]);
  */

  return {
    showOnboarding,
    isCheckingOnboarding,
    completeOnboarding,
    triggerOnboardingCheck,
    resetOnboarding,
    clearAllOnboardingFlags, // Nueva función para testing
    checkOnboardingStatus,
    showOnboardingManually,
  };
};

export default useOnboarding;
